import lodashSet from 'lodash.set';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/redux/store';
import { questionnairesApi } from 'app/services/questionnaires';

export type TRrepairsList = TRepairsItem[];

export type TRepairsItem = {
  descriptionOfRepair: Nullable<string>;
  date: Nullable<string>;
  amount: Nullable<string>;
};

export type TOtherExpensesItem = {
  descriptionOfExpense: Nullable<string>;
  date: Nullable<string>;
  amount: Nullable<string>;
};

export type TOtherExpensesList = TOtherExpensesItem[];

export interface RentalIncomeStateValues {
  isTheRentalPropertyInTheUS: Nullable<TYesOrNo>;
  typeOfProperty: Nullable<string>;
  whichStateIsTheRentalPropertyIn: Nullable<string>;
  didYouUseThePropertyYourself: Nullable<TYesOrNo>;
  isThePropertyOwnedJointlyWithYourSpouse: Nullable<TYesOrNo>;
  sinceYouAreNotFilingJointlyWhoWillReportTheRentalIncomeAndExpenses: Nullable<string>;
  rentalPropertyList: Nullable<string>;
  rentalPropertyAddress: Nullable<string>;
  purchasePrice: Nullable<string>;
  currency: Nullable<string>;
  dateWhenRentalStarted: Nullable<string>;
  doYouOwn100OfTheProperty: Nullable<TYesOrNo>;
  whatIsYourOwnershipPercentageInTheRentalProperty: Nullable<string>;
  amountOfGrossRentReceivedDuringYear: Nullable<string>;
  numberOfDaysRented365IfFullYear: Nullable<string>;
  numberOfPersonalUseDays: Nullable<string>;
  datePropertyAquiredpurchasedOrInherited: Nullable<string>;
  costOfLandIncludedInPurchasePrice: Nullable<string>;
  youCanUploadAnExcelSpreadsheetContainingTheInformationRegardingRentalExpensesInsteadOfCompletingBelow: Nullable<string>;
  advertising: Nullable<string>;
  autoTravelExpenses: Nullable<string>;
  cleaningMaintenance: Nullable<string>;
  commission: Nullable<string>;
  managementFees: Nullable<string>;
  insurance: Nullable<string>;
  legalProfessionalFees: Nullable<string>;
  mortgageInterestPaidToFinancialInstitutions: Nullable<string>;
  otherInterest: Nullable<string>;
  repairsList: TRrepairsList;
  supplies: Nullable<string>;
  howMuchIncomeTaxDidYouPayOnTheRentalIncome: Nullable<string>;
  howMuchTaxOtherThanIncomeTaxDidYouPaypropertyTaxEtc: Nullable<string>;
  utilities: Nullable<string>;
  gardeningYardMaintenance: Nullable<string>;
  otherExpensesList: TOtherExpensesList;
  notes: Nullable<string>;
  endOfRentalList: Nullable<string>;
}

export interface RentalIncomeState {
  values: RentalIncomeStateValues;
}

const initialState: RentalIncomeState = {
  values: {
    rentalPropertyList: null,
    rentalPropertyAddress: null,
    purchasePrice: null,
    currency: null,
    dateWhenRentalStarted: null,
    doYouOwn100OfTheProperty: null,
    whatIsYourOwnershipPercentageInTheRentalProperty: null,
    amountOfGrossRentReceivedDuringYear: null,
    numberOfDaysRented365IfFullYear: null,
    numberOfPersonalUseDays: null,
    datePropertyAquiredpurchasedOrInherited: null,
    costOfLandIncludedInPurchasePrice: null,
    youCanUploadAnExcelSpreadsheetContainingTheInformationRegardingRentalExpensesInsteadOfCompletingBelow:
      null,
    advertising: null,
    autoTravelExpenses: null,
    cleaningMaintenance: null,
    commission: null,
    managementFees: null,
    insurance: null,
    legalProfessionalFees: null,
    mortgageInterestPaidToFinancialInstitutions: null,
    otherInterest: null,
    repairsList: [{ descriptionOfRepair: null, date: null, amount: null }],
    supplies: null,
    howMuchIncomeTaxDidYouPayOnTheRentalIncome: null,
    howMuchTaxOtherThanIncomeTaxDidYouPaypropertyTaxEtc: null,
    utilities: null,
    gardeningYardMaintenance: null,
    otherExpensesList: [
      { descriptionOfExpense: null, date: null, amount: null },
    ],
    notes: null,
    endOfRentalList: null,
    isTheRentalPropertyInTheUS: null,
    typeOfProperty: null,
    whichStateIsTheRentalPropertyIn: null,
    didYouUseThePropertyYourself: null,
    isThePropertyOwnedJointlyWithYourSpouse: null,
    sinceYouAreNotFilingJointlyWhoWillReportTheRentalIncomeAndExpenses: null,
  },
};

const rentalIncomeSlice = createSlice({
  name: 'rentalIncome',
  initialState,
  reducers: {
    update(state, action: PayloadAction<RentalIncomeStateValues>) {
      state.values = JSON.parse(JSON.stringify(action.payload));
    },
    updateKey(
      state,
      action: PayloadAction<{
        key:
          | keyof RentalIncomeStateValues
          | keyof TRepairsItem
          | keyof TOtherExpensesItem;
        value: any;
      }>,
    ) {
      lodashSet(state, `values.${action.payload.key}`, action.payload.value);
    },
    updateFieldArrayKey(
      state,
      action: PayloadAction<{
        key:
          | keyof RentalIncomeStateValues
          | keyof TRepairsItem
          | keyof TOtherExpensesItem;
        value: TRrepairsList | TOtherExpensesList;
      }>,
    ) {
      const fieldArray = action.payload.value.map((field: any) => ({
        ...field,
      }));
      if (
        action.payload.key === 'repairsList' ||
        action.payload.key === 'otherExpensesList'
      ) {
        state.values[action.payload.key] = fieldArray;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      questionnairesApi.endpoints.getQuestionnaireById.matchFulfilled,
      (state, { payload }) => {
        state.values = payload.questionnaire.data.taxQuestionnaire
          ? payload.questionnaire.data.taxQuestionnaire.income.passiveIncome
              .rentalIncome.values
          : initialState.values;
      },
    );
  },
});

export const { update, updateKey, updateFieldArrayKey } =
  rentalIncomeSlice.actions;
export default rentalIncomeSlice.reducer;

export const selectRentalIncome = (state: RootState) =>
  state.taxQuestionnaire.income.passiveIncome.rentalIncome;
